import React from 'react';
import PropTypes from 'prop-types';
import merge from 'deepmerge';
import FocusTrap from 'focus-trap-react';

import Button from '../Button';
import Link from '../Link';
import Modal from '../Modal';
import TranslationProvider from '../../providers/TranslationProvider';

import './ErrorModal.scss';
import Icon from '../Icon';

const ErrorModalStyles = {
  content: {
    inset: '30px 40px auto',
    maxWidth: '400px',
    position: 'absolute',
  },
};

const renderButton = ({
  href,
  key,
  text,
  onClick,
  variant,
  ...props
}) => {
  const translation = (
    <TranslationProvider translationKey={key}>
      <span>{text}</span>
    </TranslationProvider>
  );
  if (href) {
    return (
      <Link key={key} href={href} variant={variant} onClick={onClick} {...props}>
        {translation}
      </Link>
    );
  }
  return (
    <Button key={key} variant={variant} onClick={onClick} {...props}>
      {translation}
    </Button>
  );
};

const ErrorModal = ({
  className,
  iconProps,
  message,
  modalStyles,
  onErrorModalClose,
  primaryCTAAdditionalProps,
  primaryCTAHref,
  primaryCTAKey,
  primaryCTAText,
  primaryCTAOnClick,
  primaryCTAVariant,
  secondaryCTAAdditionalProps,
  secondaryCTAHref,
  secondaryCTAKey,
  secondaryCTAText,
  secondaryCTAOnClick,
  secondaryCTAVariant,
  title,
  allowClickOutsideToDimiss,
}) => (
  <Modal className={`ErrorModal${className ? ` ${className}` : ''}`} isOpen styles={merge(ErrorModalStyles, modalStyles)} onRequestClose={allowClickOutsideToDimiss ? onErrorModalClose : undefined}>
    <FocusTrap>
      <div className="ErrorModal__container">
        <div className="ErrorModal__navigation">
          <Button variant="close" data-test-id="error_modal-button-close" onClick={onErrorModalClose} />
        </div>
        {iconProps?.component && (
          <div className="ErrorModal__icon">
            <Icon {...iconProps} />
          </div>
        )}
        <div className="ErrorModal__body">
          {title && <h2>{title}</h2>}

          <div className="ErrorModal__message">{message}</div>

          <div className="ErrorModal__CTAs">
            {primaryCTAText && renderButton({
              href: primaryCTAHref,
              key: primaryCTAKey,
              text: primaryCTAText,
              onClick: primaryCTAOnClick,
              variant: primaryCTAVariant,
              ...primaryCTAAdditionalProps,
            })}
            {secondaryCTAText && renderButton({
              href: secondaryCTAHref,
              key: secondaryCTAKey,
              text: secondaryCTAText,
              onClick: secondaryCTAOnClick,
              variant: secondaryCTAVariant,
              ...secondaryCTAAdditionalProps,
            })}
          </div>
        </div>
      </div>
    </FocusTrap>
  </Modal>
);

ErrorModal.propTypes = {
  className: PropTypes.string,
  iconProps: PropTypes.shape({
    component: PropTypes.element.isRequired,
    fill: PropTypes.string,
    name: PropTypes.string,
  }),
  message: PropTypes.element.isRequired,
  modalStyles: PropTypes.shape({}),
  onErrorModalClose: PropTypes.func,
  primaryCTAAdditionalProps: PropTypes.shape({}),
  primaryCTAHref: PropTypes.string,
  primaryCTAKey: PropTypes.string,
  primaryCTAText: PropTypes.string,
  primaryCTAOnClick: PropTypes.func,
  primaryCTAVariant: PropTypes.string,
  secondaryCTAAdditionalProps: PropTypes.shape({}),
  secondaryCTAHref: PropTypes.string,
  secondaryCTAKey: PropTypes.string,
  secondaryCTAText: PropTypes.string,
  secondaryCTAOnClick: PropTypes.func,
  secondaryCTAVariant: PropTypes.string,
  title: PropTypes.string,
  allowClickOutsideToDimiss: PropTypes.bool,
};

ErrorModal.defaultProps = {
  className: undefined,
  iconProps: undefined,
  modalStyles: {},
  onErrorModalClose: undefined,
  primaryCTAAdditionalProps: {},
  primaryCTAHref: undefined,
  primaryCTAKey: undefined,
  primaryCTAText: undefined,
  primaryCTAOnClick: undefined,
  primaryCTAVariant: undefined,
  secondaryCTAAdditionalProps: {},
  secondaryCTAHref: undefined,
  secondaryCTAKey: undefined,
  secondaryCTAText: undefined,
  secondaryCTAOnClick: undefined,
  secondaryCTAVariant: undefined,
  title: undefined,
  allowClickOutsideToDimiss: false,
};

ErrorModal.displayName = 'ErrorModal';

export default ErrorModal;
